import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from '../contexts/LocationContext';
import api from '../services/api';

const PurchaseReportsPage = () => {
  const navigate = useNavigate();
  const { locationId } = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });
  const [topSuppliers, setTopSuppliers] = useState([]);
  const [monthlyStats, setMonthlyStats] = useState([]);

  useEffect(() => {
    loadReports();
  }, [dateRange, locationId]);

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    try {
      // Pobierz statystyki zakupów
      const statsResponse = await api.get(`/purchase-invoices/reports/stats?date_from=${dateRange.from}&date_to=${dateRange.to}${locationId ? `&location_id=${locationId}` : ''}`);
      const statsData = statsResponse.data;
      if (statsData.success) {
        setStats(statsData.data);
      }

      // Pobierz top dostawców
      const suppliersResponse = await api.get(`/purchase-invoices/reports/top-suppliers?date_from=${dateRange.from}&date_to=${dateRange.to}&limit=10${locationId ? `&location_id=${locationId}` : ''}`);
      const suppliersData = suppliersResponse.data;
      if (suppliersData.success) {
        setTopSuppliers(suppliersData.data || []);
      }

      // Pobierz statystyki miesięczne
      const monthlyResponse = await api.get(`/purchase-invoices/reports/monthly?year=${new Date().getFullYear()}${locationId ? `&location_id=${locationId}` : ''}`);
      const monthlyData = monthlyResponse.data;
      if (monthlyData.success) {
        setMonthlyStats(monthlyData.data || []);
      }
    } catch (err) {
      console.error('Błąd ładowania raportów:', err);
      setError('Błąd ładowania raportów zakupów');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toFixed(2);
  };

  const monthNames = ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'];

  return (
    <div style={{ padding: '1rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '1rem', padding: '0.75rem 1rem',
        backgroundColor: 'var(--info)', borderRadius: '8px', color: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '1.25rem' }}>📊</span>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '600' }}>Raporty Zakupów</h2>
        </div>
        <button
          onClick={() => navigate('/purchase-invoices')}
          style={{
            padding: '0.4rem 0.8rem', fontSize: '12px', borderRadius: '4px',
            backgroundColor: 'var(--bg-secondary)', color: '#fff' , cursor: 'pointer'
          }}
        >
          ← Powrót
        </button>
      </div>

      {/* Filtry dat */}
      <div style={{
        backgroundColor: 'var(--card-bg)', padding: '0.75rem', borderRadius: '8px',
        marginBottom: '1rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '12px', fontWeight: '500' }}>Od:</label>
          <input
            type="date"
            value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            style={{ padding: '0.4rem', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '4px' }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ fontSize: '12px', fontWeight: '500' }}>Do:</label>
          <input
            type="date"
            value={dateRange.to}
            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
            style={{ padding: '0.4rem', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '4px' }}
          />
        </div>
        <button
          onClick={loadReports}
          style={{
            padding: '0.4rem 1rem', fontSize: '12px', borderRadius: '4px',
            border: '1px solid var(--info)', color: 'var(--info)' , cursor: 'pointer', fontWeight: '500'
          }}
        >
          🔄 Odśwież
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: 'var(--danger-light)', border: '1px solid var(--danger-light)', borderRadius: '4px',
          padding: '0.75rem', marginBottom: '1rem', color: 'var(--danger)', fontSize: '13px'
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
          Ładowanie raportów...
        </div>
      ) : (
        <>
          {/* Statystyki główne */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem', marginBottom: '1rem'
          }}>
            <div style={{
              backgroundColor: 'var(--card-bg)', padding: '1rem', borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid var(--cyan)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Liczba faktur</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--info)' }}>
                {stats?.total_invoices || 0}
              </div>
            </div>
            <div style={{
              backgroundColor: 'var(--card-bg)', padding: '1rem', borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid var(--success)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Suma netto</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--success)' }}>
                {formatCurrency(stats?.total_netto)} zł
              </div>
            </div>
            <div style={{
              backgroundColor: 'var(--card-bg)', padding: '1rem', borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid var(--warning)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Suma VAT</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--warning)' }}>
                {formatCurrency(stats?.total_vat)} zł
              </div>
            </div>
            <div style={{
              backgroundColor: 'var(--card-bg)', padding: '1rem', borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid var(--danger)'
            }}>
              <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Suma brutto</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--danger)' }}>
                {formatCurrency(stats?.total_brutto)} zł
              </div>
            </div>
          </div>

          {/* Dwie kolumny */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Top dostawcy */}
            <div style={{
              backgroundColor: 'var(--card-bg)', borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden'
            }}>
              <div style={{
                padding: '0.75rem', backgroundColor: 'var(--bg)',
                borderBottom: '1px solid var(--border)', fontWeight: '600', fontSize: '13px'
              }}>
                🏭 Top 10 dostawców
              </div>
              <div style={{ padding: '0.5rem' }}>
                {topSuppliers.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'var(--bg)' }}>
                        <th style={{ padding: '0.4rem', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>#</th>
                        <th style={{ padding: '0.4rem', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Dostawca</th>
                        <th style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>Faktur</th>
                        <th style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>Suma brutto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topSuppliers.map((supplier, idx) => (
                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                          <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)' }}>{idx + 1}</td>
                          <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', fontWeight: '500' }}>
                            {supplier.dostawca_nazwa || 'Nieznany'}
                          </td>
                          <td style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>
                            {supplier.invoices_count}
                          </td>
                          <td style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid var(--border)', fontWeight: '600', color: 'var(--success)' }}>
                            {formatCurrency(supplier.total_brutto)} zł
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                    Brak danych
                  </div>
                )}
              </div>
            </div>

            {/* Statystyki miesięczne */}
            <div style={{
              backgroundColor: 'var(--card-bg)', borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden'
            }}>
              <div style={{
                padding: '0.75rem', backgroundColor: 'var(--bg)',
                borderBottom: '1px solid var(--border)', fontWeight: '600', fontSize: '13px'
              }}>
                📅 Zakupy miesięczne ({new Date().getFullYear()})
              </div>
              <div style={{ padding: '0.5rem' }}>
                {monthlyStats.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'var(--bg)' }}>
                        <th style={{ padding: '0.4rem', textAlign: 'left', borderBottom: '1px solid var(--border)' }}>Miesiąc</th>
                        <th style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>Faktur</th>
                        <th style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>Netto</th>
                        <th style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>Brutto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyStats.map((month, idx) => (
                        <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                          <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', fontWeight: '500' }}>
                            {monthNames[month.month - 1]} {month.year}
                          </td>
                          <td style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>
                            {month.invoices_count}
                          </td>
                          <td style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid var(--border)' }}>
                            {formatCurrency(month.total_netto)} zł
                          </td>
                          <td style={{ padding: '0.4rem', textAlign: 'right', borderBottom: '1px solid var(--border)', fontWeight: '600', color: 'var(--success)' }}>
                            {formatCurrency(month.total_brutto)} zł
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                    Brak danych
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PurchaseReportsPage;
